import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLogs, exportAuditLogs, getSecurityEvents, getUsersLookup } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  History as HistoryIcon,
  FileText,
  User,
  Filter,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Search,
  Download,
  X,
  ShieldAlert,
  Globe,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import Pagination from '../components/Pagination';
import { toast } from 'sonner';

const PAGE_SIZE = 10;

const ACTION_CONFIG = {
  create: { label: 'Criação', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  update: { label: 'Atualização', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  delete: { label: 'Exclusão', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  upload: { label: 'Upload de anexo', className: 'bg-violet-50 text-violet-700 border-violet-200' },
  attachment_delete: { label: 'Exclusão de anexo', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  password_change: { label: 'Troca de senha', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  password_reset: { label: 'Reset de senha', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  permissions_update: { label: 'Permissões alteradas', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  mfa_enabled: { label: '2FA ativado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  mfa_disabled: { label: '2FA desativado', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  mfa_admin_reset: { label: '2FA resetado (admin)', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  import: { label: 'Importação', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
};

const SECURITY_EVENT_LABELS = {
  login_success: { label: 'Login realizado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  login_failed: { label: 'Falha de login', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  login_locked: { label: 'Conta bloqueada (tentativas)', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  login_inactive: { label: 'Login de conta inativa', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  login_mfa_required: { label: 'Desafio 2FA solicitado', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  logout: { label: 'Logout', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  mfa_enabled: { label: '2FA ativado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  mfa_disabled: { label: '2FA desativado', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  mfa_failed: { label: 'Código 2FA inválido', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  mfa_rate_limited: { label: '2FA: excesso de tentativas', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  mfa_recovery_code_used: { label: 'Código de recuperação usado', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  mfa_admin_reset: { label: '2FA resetado (admin)', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  refresh_token_reuse: { label: '⚠ Reuso de token (sessão revogada)', className: 'bg-rose-100 text-rose-800 border-rose-300' },
  password_change: { label: 'Troca de senha', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  password_change_failed: { label: 'Falha na troca de senha', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  password_reset: { label: 'Reset de senha (admin)', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  permission_change: { label: 'Permissões alteradas', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  profile_update: { label: 'Perfil atualizado', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  session_revoked: { label: 'Sessão encerrada', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  sessions_revoked_others: { label: 'Outras sessões encerradas', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  user_create: { label: 'Usuário criado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  user_delete: { label: 'Usuário excluído', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  user_register: { label: 'Usuário registrado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const FIELD_LABELS = {
  name: 'Nome', type: 'Tipo', description: 'Descrição', issuing_authority: 'Órgão Emissor',
  license_number: 'Número/CNPJ', issue_date: 'Data de Emissão', expiry_date: 'Data de Vencimento',
  responsible_person: 'Responsável', notes: 'Observações', role: 'Perfil', is_active: 'Status',
  category: 'Categoria', email: 'Email', file_name: 'Arquivo', target_email: 'Usuário alvo',
  is_critical: 'Item Crítico', indeterminate_expiry: 'Validade Indeterminada',
  notification_recipients: 'Destinatários de Notificação',
  notification_windows_before: 'Janelas antes do vencimento',
  notification_windows_after: 'Janelas após o vencimento',
};

const formatDateTime = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean' || key === 'is_active' || key === 'is_critical' || key === 'indeterminate_expiry') {
    if (key === 'is_active') return value ? 'Ativo' : 'Inativo';
    return value ? 'Sim' : 'Não';
  }
  if (key === 'role') return ({ admin: 'Administrador', gestor: 'Gestor', usuario: 'Usuário' })[value] || value;
  if (key === 'type') return ({ ambiental: 'Ambiental', alvara: 'Alvará', sanitaria: 'Sanitária' })[value] || value;
  if (key === 'category') return ({ kn: 'KN', fornecedor: 'Fornecedor' })[value] || value;
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (key.includes('date') && value) {
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }
  return String(value);
};

const getActionBadge = (action) => {
  const { label, className } = ACTION_CONFIG[action] || { label: action, className: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <Badge variant="outline" className={className}>{label}</Badge>;
};

const History = () => {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('activity');

  // ---- Atividades ----
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);

  // filtros
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const buildParams = useCallback(() => {
    const params = {};
    if (entityFilter !== 'all') params.entity_type = entityFilter;
    if (actionFilter !== 'all') params.action = actionFilter;
    if (userFilter !== 'all') params.user_id = userFilter;
    if (dateStart) params.date_start = dateStart;
    if (dateEnd) params.date_end = dateEnd;
    if (search) params.search = search;
    return params;
  }, [entityFilter, actionFilter, userFilter, dateStart, dateEnd, search]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAuditLogs({ ...buildParams(), page, page_size: PAGE_SIZE });
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [buildParams, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [entityFilter, actionFilter, userFilter, dateStart, dateEnd, search]);

  useEffect(() => {
    getUsersLookup().then(res => setUsers(res.data || [])).catch(() => {});
  }, []);

  const hasActiveFilters = entityFilter !== 'all' || actionFilter !== 'all' || userFilter !== 'all' || dateStart || dateEnd || search;

  const clearFilters = () => {
    setEntityFilter('all'); setActionFilter('all'); setUserFilter('all');
    setDateStart(''); setDateEnd(''); setSearchInput(''); setSearch('');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAuditLogs(buildParams());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'historico_auditoria.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Histórico exportado');
    } catch (err) {
      toast.error('Erro ao exportar histórico');
    } finally {
      setExporting(false);
    }
  };

  const toggleExpand = (logId) => setExpandedLogs(prev => ({ ...prev, [logId]: !prev[logId] }));

  // ---- Segurança ----
  const [secEvents, setSecEvents] = useState([]);
  const [secLoading, setSecLoading] = useState(false);
  const [secTypeFilter, setSecTypeFilter] = useState('all');
  const [secPage, setSecPage] = useState(1);

  useEffect(() => {
    if (tab !== 'security') return;
    setSecLoading(true);
    const params = { limit: 200 };
    if (secTypeFilter !== 'all') params.event_type = secTypeFilter;
    getSecurityEvents(params)
      .then(res => setSecEvents(res.data || []))
      .catch(() => toast.error('Erro ao carregar eventos de segurança'))
      .finally(() => setSecLoading(false));
  }, [tab, secTypeFilter]);

  useEffect(() => { setSecPage(1); }, [secTypeFilter, tab]);

  const secPageItems = secEvents.slice((secPage - 1) * PAGE_SIZE, secPage * PAGE_SIZE);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const renderChangeRow = (key, value) => {
    const isDiff = value && typeof value === 'object' && !Array.isArray(value) && ('old' in value || 'new' in value);
    return (
      <div key={key} className="flex items-center gap-2 text-sm flex-wrap">
        <span className="text-gray-500 min-w-[160px]">{FIELD_LABELS[key] || key}:</span>
        {isDiff ? (
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-500 font-mono text-xs bg-rose-50 border border-rose-100 px-2 py-1 rounded line-through decoration-rose-300">
              {formatValue(key, value.old)}
            </span>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-900 font-mono text-xs bg-emerald-50 border border-emerald-100 px-2 py-1 rounded">
              {formatValue(key, value.new)}
            </span>
          </span>
        ) : (
          <span className="text-gray-900 font-mono text-xs bg-white px-2 py-1 rounded border border-gray-200">
            {formatValue(key, value)}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Histórico de Modificações</h1>
        <p className="text-gray-500 mt-1">Acompanhe todas as alterações e eventos do sistema</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {isAdmin() && (
          <TabsList className="bg-gray-100">
            <TabsTrigger value="activity" data-testid="tab-activity">
              <HistoryIcon className="w-4 h-4 mr-2" /> Atividades
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <ShieldAlert className="w-4 h-4 mr-2" /> Segurança
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="activity" className="space-y-4 mt-4">
          {/* Filtros */}
          <Card className="bg-white border-gray-200 shadow-md">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                  <SelectTrigger className="w-[150px] bg-white border-gray-300 text-gray-700" data-testid="entity-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-xl">
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="license">Licenças</SelectItem>
                    <SelectItem value="user">Usuários</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-700" data-testid="action-filter">
                    <SelectValue placeholder="Ação" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-xl max-h-64">
                    <SelectItem value="all">Todas as ações</SelectItem>
                    {Object.entries(ACTION_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-[200px] bg-white border-gray-300 text-gray-700" data-testid="user-filter">
                    <SelectValue placeholder="Usuário" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-xl max-h-64">
                    <SelectItem value="all">Todos os usuários</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Input
                    type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)}
                    className="w-[150px] bg-white border-gray-300 text-gray-700" data-testid="date-start-filter"
                  />
                  <span className="text-gray-400 text-sm">até</span>
                  <Input
                    type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)}
                    className="w-[150px] bg-white border-gray-300 text-gray-700" data-testid="date-end-filter"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <form
                  className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md"
                  onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}
                >
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por item ou usuário..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="pl-9 bg-white border-gray-300 text-gray-700"
                      data-testid="history-search-input"
                    />
                  </div>
                  <Button type="submit" variant="outline" size="sm" className="bg-white border-gray-300 text-gray-700" data-testid="history-search-btn">
                    Buscar
                  </Button>
                </form>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 hover:text-gray-700" data-testid="clear-filters-btn">
                    <X className="w-3.5 h-3.5 mr-1" /> Limpar filtros
                  </Button>
                )}
                <div className="ml-auto">
                  <Button
                    variant="outline" size="sm" onClick={handleExport} disabled={exporting}
                    className="bg-white border-gray-300 text-gray-700"
                    data-testid="export-history-btn"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    {exporting ? 'Exportando...' : 'Exportar Excel'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logs */}
          <Card className="bg-white border-gray-200 shadow-lg">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg text-gray-900 flex items-center justify-between font-semibold">
                <span className="flex items-center gap-2">
                  <HistoryIcon className="w-5 h-5 text-[#003366]" />
                  Registros de Atividade
                </span>
                <span className="text-sm font-normal text-gray-500" data-testid="history-total">
                  {total} registro(s)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(8)].map((_, i) => (<Skeleton key={i} className="h-20 bg-gray-100" />))}
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <HistoryIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum registro encontrado</h3>
                  <p>{hasActiveFilters ? 'Ajuste os filtros para ver mais resultados' : 'As modificações do sistema aparecerão aqui'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => {
                    const isExpanded = expandedLogs[log.id];
                    const changeEntries = Object.entries(log.changes || {}).filter(([k]) => k !== 'updated_at' && k !== 'status');
                    const hasChanges = changeEntries.length > 0;

                    return (
                      <div
                        key={log.id}
                        className="p-4 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-md transition-all"
                        data-testid={`log-item-${log.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 min-w-0">
                            <div className={`p-2 rounded-lg flex-shrink-0 ${
                              log.action === 'create' ? 'bg-emerald-100' :
                              log.action === 'delete' ? 'bg-rose-100' :
                              log.action === 'upload' ? 'bg-violet-100' : 'bg-blue-100'
                            }`}>
                              {log.entity_type === 'license' ? (
                                <FileText className={`w-5 h-5 ${
                                  log.action === 'create' ? 'text-emerald-600' :
                                  log.action === 'delete' ? 'text-rose-600' :
                                  log.action === 'upload' ? 'text-violet-600' : 'text-blue-600'
                                }`} />
                              ) : (
                                <User className={`w-5 h-5 ${
                                  log.action === 'create' ? 'text-emerald-600' :
                                  log.action === 'delete' ? 'text-rose-600' :
                                  log.action === 'upload' ? 'text-violet-600' : 'text-blue-600'
                                }`} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {getActionBadge(log.action)}
                                <span className="text-gray-500 text-sm">em</span>
                                <Badge variant="outline" className="bg-white text-gray-700 border-gray-300">
                                  {log.entity_type === 'license' ? 'Licença' : log.entity_type === 'user' ? 'Usuário' : log.entity_type}
                                </Badge>
                                {log.entity_name && (
                                  <span className="text-sm font-semibold text-gray-800 truncate" data-testid={`log-entity-name-${log.id}`}>
                                    {log.entity_name}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-2 text-sm flex-wrap">
                                <span className="text-gray-600 flex items-center gap-1">
                                  <User className="w-3 h-3" /> {log.user_name}
                                </span>
                                <span className="text-gray-500 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" /> {formatDateTime(log.timestamp)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {hasChanges && (
                            <Button
                              variant="ghost" size="sm" onClick={() => toggleExpand(log.id)}
                              className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
                              data-testid={`expand-log-${log.id}`}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>

                        {isExpanded && hasChanges && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Campos alterados:</p>
                            <div className="space-y-2">
                              {changeEntries.map(([key, value]) => renderChangeRow(key, value))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Paginação */}
              {!loading && total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <span className="text-sm text-gray-500">
                    Página {page} de {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm" disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                      className="bg-white border-gray-300 text-gray-700"
                      data-testid="history-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <Button
                      variant="outline" size="sm" disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="bg-white border-gray-300 text-gray-700"
                      data-testid="history-next-page"
                    >
                      Próxima <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4 mt-4">
          <Card className="bg-white border-gray-200 shadow-md">
            <CardContent className="p-4 flex items-center gap-3">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={secTypeFilter} onValueChange={setSecTypeFilter}>
                <SelectTrigger className="w-[260px] bg-white border-gray-300 text-gray-700" data-testid="security-type-filter">
                  <SelectValue placeholder="Tipo de evento" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-xl max-h-72">
                  <SelectItem value="all">Todos os eventos</SelectItem>
                  {Object.entries(SECURITY_EVENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-lg">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
                <ShieldAlert className="w-5 h-5 text-[#003366]" />
                Eventos de Segurança
                <span className="text-sm font-normal text-gray-500 ml-auto">últimos {secEvents.length} evento(s)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {secLoading ? (
                <div className="space-y-3">
                  {[...Array(8)].map((_, i) => (<Skeleton key={i} className="h-14 bg-gray-100" />))}
                </div>
              ) : secEvents.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <ShieldAlert className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Nenhum evento de segurança encontrado</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="security-events-list">
                  {secPageItems.map((ev, idx) => {
                    const cfg = SECURITY_EVENT_LABELS[ev.event_type] || { label: ev.event_type, className: 'bg-gray-100 text-gray-600 border-gray-200' };
                    return (
                      <div key={ev.id || idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100" data-testid={`security-event-${idx}`}>
                        <ShieldAlert className="w-4 h-4 text-[#003366] mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={cfg.className}>{cfg.label}</Badge>
                            {ev.user_email && <span className="text-sm text-gray-700 font-medium">{ev.user_email}</span>}
                            {ev.target && <span className="text-sm text-gray-500">→ {ev.target}</span>}
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateTime(ev.timestamp)}</span>
                            {ev.ip && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {ev.ip}</span>}
                            {ev.user_agent && <span className="truncate max-w-[300px]" title={ev.user_agent}>{ev.user_agent.slice(0, 60)}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!secLoading && (
                <Pagination page={secPage} pageSize={PAGE_SIZE} total={secEvents.length} onPageChange={setSecPage} testId="security-pagination" />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default History;
